import { CartUpdateEvent } from '@theme/events';

/**
 * Page de demande de devis (poids ≥ seuil).
 *  - source=product : ajoute au récapitulatif la variante transmise en URL (product, variant, qty)
 *    en plus des lignes du panier rendues côté serveur ;
 *  - après envoi réussi (message de succès présent) : vide le panier.
 */
class QuoteRequestComponent extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('[ref="successMessage"]')) {
      this.#clearCart();
      return;
    }
    this.#hydrateFromUrl();
  }

  get root() {
    return this.dataset.root || '/';
  }

  /** @param {string} ref */
  #ref(ref) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[ref="${ref}"]`));
  }

  async #hydrateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source') || 'cart';
    const sourceField = /** @type {HTMLInputElement | null} */ (this.#ref('sourceField'));
    if (sourceField) sourceField.value = source;

    if (source !== 'product') {
      this.#syncFields();
      return;
    }

    const handle = params.get('product');
    const variantId = params.get('variant');
    const qty = Math.max(1, parseInt(params.get('qty') || '1', 10) || 1);
    if (!handle) return;

    const loading = this.#ref('loadingMessage');
    if (loading) loading.hidden = false;

    try {
      const response = await fetch(`${this.root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const product = await response.json();
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant =
        variants.find((v) => String(v.id) === String(variantId)) ?? variants.find((v) => v.available) ?? variants[0];
      if (!variant) throw new Error('variant not found');

      const isDefaultOnly = variants.length === 1 && variant.title === 'Default Title';
      this.#upsertRow({
        variantId: String(variant.id),
        productTitle: product.title || '',
        variantTitle: isDefaultOnly ? '' : variant.title || '',
        sku: variant.sku || '',
        grams: Number(variant.weight) || 0,
        qty,
        image: variant.featured_image?.src || product.featured_image || '',
      });
    } catch (error) {
      console.error('[quote-request] impossible de charger le produit', error);
    } finally {
      if (loading) loading.hidden = true;
      this.#syncFields();
    }
  }

  /**
   * @param {{variantId: string, productTitle: string, variantTitle: string, sku: string, grams: number, qty: number, image: string}} line
   */
  #upsertRow(line) {
    const body = this.#ref('summaryBody');
    if (!body) return;

    const existing = /** @type {HTMLTableRowElement | null} */ (
      body.querySelector(`tr[data-variant-id="${line.variantId}"]`)
    );
    if (existing) {
      const newQty = (Number(existing.dataset.qty) || 0) + line.qty;
      existing.dataset.qty = String(newQty);
      const cells = existing.querySelectorAll('td');
      if (cells[1]) cells[1].textContent = String(newQty);
      if (cells[3]) cells[3].textContent = `${this.#kg(line.grams * newQty)} kg`;
      return;
    }

    const labels = this.#columnLabels();
    const tr = document.createElement('tr');
    tr.dataset.variantId = line.variantId;
    tr.dataset.grams = String(line.grams);
    tr.dataset.qty = String(line.qty);

    const productCell = document.createElement('td');
    const wrapper = document.createElement('div');
    wrapper.className = 'quote-request__product';
    if (line.image) {
      const img = document.createElement('img');
      img.src = line.image;
      img.alt = '';
      img.width = 60;
      img.height = 60;
      img.loading = 'lazy';
      img.className = 'quote-request__thumb';
      wrapper.appendChild(img);
    }
    const text = document.createElement('div');
    const title = document.createElement('span');
    title.className = 'quote-request__product-title';
    title.textContent = line.productTitle;
    text.appendChild(title);
    if (line.variantTitle) {
      const variant = document.createElement('span');
      variant.className = 'quote-request__product-variant';
      variant.textContent = line.variantTitle;
      text.appendChild(variant);
    }
    if (line.sku) {
      const sku = document.createElement('span');
      sku.className = 'quote-request__product-sku';
      sku.textContent = `SKU ${line.sku}`;
      text.appendChild(sku);
    }
    wrapper.appendChild(text);
    productCell.appendChild(wrapper);
    tr.appendChild(productCell);

    const values = [String(line.qty), `${this.#kg(line.grams)} kg`, `${this.#kg(line.grams * line.qty)} kg`];
    values.forEach((value, index) => {
      const td = document.createElement('td');
      td.className = 'quote-request__num';
      td.dataset.label = labels[index] || '';
      td.textContent = value;
      tr.appendChild(td);
    });

    body.appendChild(tr);
  }

  #columnLabels() {
    const table = this.#ref('summaryTable');
    const heads = table ? Array.from(table.querySelectorAll('thead th')) : [];
    return heads.slice(1).map((th) => (th.textContent || '').trim());
  }

  /** Recalcule le total et les champs cachés repris dans l'e-mail */
  #syncFields() {
    const body = this.#ref('summaryBody');
    const rows = body ? Array.from(body.querySelectorAll('tr')) : [];
    const table = this.#ref('summaryTable');
    const empty = this.#ref('emptyMessage');
    const submit = /** @type {HTMLButtonElement | null} */ (this.#ref('submitButton'));

    if (table) table.hidden = rows.length === 0;
    if (empty) empty.hidden = rows.length > 0;
    if (submit) submit.disabled = rows.length === 0;

    let totalGrams = 0;
    const lines = rows.map((row) => {
      const grams = Number(row.dataset.grams) || 0;
      const qty = Number(row.dataset.qty) || 0;
      totalGrams += grams * qty;
      const title = row.querySelector('.quote-request__product-title')?.textContent?.trim() || '';
      const variant = row.querySelector('.quote-request__product-variant')?.textContent?.trim() || '';
      const sku = row.querySelector('.quote-request__product-sku')?.textContent?.trim() || '';
      return `- ${title}${variant ? ` / ${variant}` : ''}${sku ? ` [${sku}]` : ''} x ${qty} — ${this.#kg(
        grams * qty
      )} kg (ID variante ${row.dataset.variantId})`;
    });

    const totalWeight = this.#ref('totalWeight');
    if (totalWeight) totalWeight.textContent = this.#kg(totalGrams, 1);

    const productsField = /** @type {HTMLTextAreaElement | null} */ (this.#ref('productsField'));
    if (productsField) productsField.value = lines.join('\n');

    const totalWeightField = /** @type {HTMLInputElement | null} */ (this.#ref('totalWeightField'));
    if (totalWeightField) totalWeightField.value = `${this.#kg(totalGrams, 1)} kg`;
  }

  /**
   * @param {number} grams
   * @param {number} [decimals]
   */
  #kg(grams, decimals = 2) {
    return (grams / 1000).toLocaleString(document.documentElement.lang || 'fr', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
  }

  async #clearCart() {
    try {
      const response = await fetch(`${this.root}cart/clear.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'quote-request', { itemCount: cart.item_count ?? 0, source: 'quote-request' })
      );
    } catch (error) {
      console.error('[quote-request] impossible de vider le panier', error);
    }
  }
}

if (!customElements.get('quote-request-component')) {
  customElements.define('quote-request-component', QuoteRequestComponent);
}
